import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtMoney, formatApiErrorDetail } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { PhoneInput } from "../components/PhoneInput";
import { ShortVideoInput } from "../components/ShortVideoInput";
import { WA_COUNTRY_NAMES } from "../lib/westafrica";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UploadSimple, X, Sparkle, Tag, Calendar, House, FileText, MapPin, CheckCircle } from "@phosphor-icons/react";

const TYPES = [["apartment", "Appartement"], ["house", "Maison"], ["villa", "Villa"], ["land", "Terrain"], ["hall", "Salle / Espace"]];
const AMENITIES = ["Wifi", "Climatisation", "Piscine", "Parking", "Sécurité 24h", "Générateur", "Cuisine équipée", "Jardin", "Ascenseur", "Meublé"];
const EVENT_CASES = ["Soirées", "Réceptions", "Mariages", "Concerts", "Matchs de foot", "Séminaires", "Anniversaires", "Shootings"];
const MODES = [["sale", "Vente classique"], ["longterm", "Location longue durée"], ["event", "Location courte durée"]];

// Défini HORS du composant pour éviter le remontage des champs à chaque frappe (bug clavier).
const Section = ({ title, children }) => (
  <div className="bg-white border border-stone-200 rounded-2xl p-6">
    <h2 className="font-display text-lg font-bold text-stone-900 mb-4">{title}</h2>
    {children}
  </div>
);

export default function AddProperty() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [f, setF] = useState({
    title: "", description: "", property_type: "apartment", country: "", city: "", address: "",
    surface: "", rooms: "", bathrooms: "", contact_phone: "", amenities: [], media: [],
    modes: ["sale"], sale_price: "", monthly_rent: "", event_price_per_day: "", event_use_cases: [],
    video_url: "", short_videos: [], ownership_document: "",
  });

  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const onFiles = (e) => {
    const files = Array.from(e.target.files).slice(0, 6);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setF((s) => ({ ...s, media: [...s.media, reader.result] }));
      reader.readAsDataURL(file);
    });
  };

  const onDoc = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setF((s) => ({ ...s, ownership_document: reader.result }));
    reader.readAsDataURL(file);
  };

  const estimateRent = async () => {
    setEstimating(true);
    try {
      const { data } = await api.post("/ai/estimate-rent", {
        city: f.city, surface: Number(f.surface), rooms: Number(f.rooms), property_type: f.property_type,
      });
      up("monthly_rent", String(data.suggested_rent));
      toast.success(`Loyer suggéré: ${fmtMoney(data.suggested_rent)}. ${data.reasoning}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEstimating(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (f.modes.length === 0) { toast.error("Choisissez au moins un mode d'exploitation."); return; }
    if (!f.country) { toast.error("Sélectionnez le pays du bien."); return; }
    if (!f.ownership_document) { toast.error("Un document de propriété est requis pour la certification."); return; }
    setSaving(true);
    try {
      await api.post("/properties", {
        ...f,
        surface: Number(f.surface) || 0, rooms: Number(f.rooms) || 0, bathrooms: Number(f.bathrooms) || 0,
        sale_price: Number(f.sale_price) || 0, monthly_rent: Number(f.monthly_rent) || 0,
        event_price_per_day: Number(f.event_price_per_day) || 0,
      });
      toast.success("Bien publié ! Il sera certifié après vérification du document.");
      navigate("/dashboard");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <span className="overline text-xs text-orange-800">Nouveau bien</span>
        <h1 className="font-display text-4xl font-bold text-stone-900 mt-1 mb-6">Publier un bien</h1>

        <form onSubmit={submit} className="space-y-5">
          <Section title="Galerie média">
            <label data-testid="media-upload" className="border-2 border-dashed border-stone-300 rounded-xl p-8 flex flex-col items-center cursor-pointer hover:border-orange-400 transition-colors">
              <UploadSimple size={28} className="text-orange-800" />
              <span className="text-sm text-stone-500 mt-2">Importer photos / vidéos (max 6)</span>
              <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={onFiles} />
            </label>
            {f.media.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {f.media.map((m, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={m} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => up("media", f.media.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4">
              <Label>Lien vidéo HD (YouTube ou MP4)</Label>
              <Input data-testid="prop-video" value={f.video_url} onChange={(e) => up("video_url", e.target.value)} className="mt-1" placeholder="https://youtu.be/… ou https://…/visite.mp4" />
            </div>
            <ShortVideoInput value={f.short_videos} onChange={(v) => up("short_videos", v)} />
          </Section>

          <Section title="Localisation">
            <Label className="flex items-center gap-1"><MapPin size={14} /> Pays (Afrique de l'Ouest — CEDEAO) *</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="country-grid">
              {WA_COUNTRY_NAMES.map((c) => (
                <button key={c} type="button" data-testid={`country-${c}`} onClick={() => up("country", c)}
                  className={`text-sm rounded-xl border-2 px-3 py-2 text-left transition-colors ${f.country === c ? "border-orange-800 bg-orange-50 text-orange-800 font-semibold" : "border-stone-200 text-stone-600 hover:border-orange-300"}`}>
                  {f.country === c && <CheckCircle weight="fill" size={13} className="inline mr-1" />}{c}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div><Label>Ville *</Label><Input data-testid="prop-city" required value={f.city} onChange={(e) => up("city", e.target.value)} className="mt-1" placeholder="Ex : Dakar" disabled={!f.country} /></div>
              <div><Label>Adresse / Quartier</Label><Input value={f.address} onChange={(e) => up("address", e.target.value)} className="mt-1" placeholder="Quartier, rue…" /></div>
            </div>
          </Section>

          <Section title="Description & caractéristiques">
            <div className="space-y-4">
              <div><Label>Titre *</Label><Input data-testid="prop-title" required value={f.title} onChange={(e) => up("title", e.target.value)} className="mt-1" placeholder="Villa moderne avec piscine" /></div>
              <div><Label>Description</Label><Textarea data-testid="prop-desc" value={f.description} onChange={(e) => up("description", e.target.value)} className="mt-1" rows={4} /></div>
              <div><Label>Type de bien</Label>
                <Select value={f.property_type} onValueChange={(v) => up("property_type", v)}>
                  <SelectTrigger data-testid="prop-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Surface (m²)</Label><Input data-testid="prop-surface" type="number" value={f.surface} onChange={(e) => up("surface", e.target.value)} className="mt-1" /></div>
                <div><Label>Pièces</Label><Input data-testid="prop-rooms" type="number" value={f.rooms} onChange={(e) => up("rooms", e.target.value)} className="mt-1" /></div>
                <div><Label>Salles de bain</Label><Input type="number" value={f.bathrooms} onChange={(e) => up("bathrooms", e.target.value)} className="mt-1" /></div>
              </div>
              <div><Label>Téléphone de contact</Label><div className="mt-1"><PhoneInput testid="prop-phone" value={f.contact_phone} onChange={(v) => up("contact_phone", v)} /></div></div>
            </div>
            <div className="mt-4">
              <Label className="flex items-center gap-1"><Tag size={14} /> Équipements</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {AMENITIES.map((a) => (
                  <button key={a} type="button" onClick={() => toggle("amenities", a)}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${f.amenities.includes(a) ? "bg-orange-800 text-white border-orange-800" : "bg-white text-stone-600 border-stone-200"}`}>{a}</button>
                ))}
              </div>
            </div>
          </Section>

          <Section title="Certification de propriété">
            <p className="text-sm text-stone-500 -mt-2 mb-3">Pour limiter la fraude, un document prouvant que vous êtes bien le propriétaire est requis (titre foncier, bail, acte de vente…).</p>
            <label data-testid="doc-upload" className="border-2 border-dashed border-stone-300 rounded-xl p-6 flex flex-col items-center cursor-pointer hover:border-orange-400 transition-colors">
              <FileText size={26} className="text-orange-800" />
              <span className="text-sm text-stone-500 mt-2">{f.ownership_document ? "Document ajouté ✓ (cliquer pour remplacer)" : "Importer le document (image ou PDF) *"}</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onDoc} />
            </label>
          </Section>

          <Section title="Mode d'exploitation">
            <p className="text-sm text-stone-500 -mt-2 mb-3">Sélectionnez un ou plusieurs modes pour ce bien.</p>
            <div className="space-y-3">
              {MODES.map(([v, l]) => (
                <div key={v} className={`rounded-xl border-2 p-4 transition-colors ${f.modes.includes(v) ? "border-orange-800 bg-orange-50/50" : "border-stone-200"}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input data-testid={`mode-${v}`} type="checkbox" checked={f.modes.includes(v)} onChange={() => toggle("modes", v)} className="w-4 h-4 accent-orange-800" />
                    <span className="font-semibold text-stone-900">{l}</span>
                  </label>
                  {f.modes.includes(v) && (
                    <div className="mt-3 pl-6">
                      {v === "sale" && <div><Label>Prix de vente (FCFA)</Label><Input data-testid="sale-price" type="number" value={f.sale_price} onChange={(e) => up("sale_price", e.target.value)} className="mt-1" /></div>}
                      {v === "longterm" && (
                        <div className="flex items-end gap-2">
                          <div className="flex-1"><Label>Loyer mensuel (FCFA)</Label><Input data-testid="rent-price" type="number" value={f.monthly_rent} onChange={(e) => up("monthly_rent", e.target.value)} className="mt-1" /></div>
                          <Button type="button" data-testid="estimate-rent-btn" onClick={estimateRent} disabled={estimating} variant="outline" className="border-orange-300 text-orange-800 h-10"><Sparkle size={15} className="mr-1" />{estimating ? "…" : "Estimer IA"}</Button>
                        </div>
                      )}
                      {v === "event" && (
                        <div className="space-y-3">
                          <div><Label className="flex items-center gap-1"><Calendar size={14} /> Tarif par jour / nuitée (FCFA)</Label><Input data-testid="event-price" type="number" value={f.event_price_per_day} onChange={(e) => up("event_price_per_day", e.target.value)} className="mt-1" /></div>
                          <div><Label>Cas d'usage</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {EVENT_CASES.map((c) => (
                                <button key={c} type="button" onClick={() => toggle("event_use_cases", c)}
                                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${f.event_use_cases.includes(c) ? "bg-amber-600 text-white border-amber-600" : "bg-white text-stone-600 border-stone-200"}`}>{c}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>

          <Button data-testid="publish-btn" type="submit" disabled={saving} className="w-full rounded-full bg-orange-800 hover:bg-orange-900 text-white h-12">
            <House weight="fill" className="mr-2" />{saving ? "Publication…" : "Publier le bien"}
          </Button>
        </form>
      </div>
    </div>
  );
}
